// Admin-wide dialog primitives. Single source of truth — every Founder OS
// and Opportunity Engine dialog renders inside ModalShell with Field rows
// for form layout. Promoted here so the two sibling feature trees stay in
// sync visually + behaviorally.

import { useEffect } from "react";
import { X as XIcon } from "lucide-react";

export function ModalShell({ title, eyebrow, onClose, children, footer, testId, size = "md" }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const widthCls =
    size === "lg" ? "max-w-3xl" : size === "xl" ? "max-w-5xl" : "max-w-lg";

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-stretch justify-center overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`w-full ${widthCls} my-10 mx-4 rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col`}
      >
        <div className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700">
                {eyebrow}
              </div>
            )}
            <div className="font-display font-bold text-slate-900 truncate">{title}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full w-8 h-8 inline-flex items-center justify-center text-slate-500 hover:bg-slate-100"
            data-testid={`${testId}-close`}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">{children}</div>
        {footer && (
          <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
